/**
 * /auth/reset — Supabase password-recovery landing page.
 *
 * RC 1.35.F2 Stage-1 P0-5.
 *
 * When the user clicks the recovery link in their email, Supabase
 * lands them here with `#access_token=...&type=recovery` in the URL
 * fragment. Supabase JS automatically picks the fragment up via
 * `onAuthStateChange("PASSWORD_RECOVERY", session)` and exposes the
 * scoped recovery session.
 *
 * We block the form until the recovery session is established. From
 * inside that session, `supabase.auth.updateUser({ password })` sets
 * the new password. On success, we sign the user out (so the
 * recovery session is cleaned up) and redirect to `/login` with a
 * one-shot success toast.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import PasswordInput from '@/components/PasswordInput';
import BrandLogo from '@/components/BrandLogo';
import LegalFooter from '@/components/LegalFooter';
import { Loader2, KeyRound, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const MIN_PASSWORD_LEN = 8;

export default function ResetPasswordPage() {
  const navigate = useNavigate();

  // recovery-session lifecycle:
  //   'pending'  → waiting for Supabase to expose the recovery session
  //   'ready'    → form usable
  //   'invalid'  → no recovery session detected; bad / expired link
  const [phase,        setPhase]        = useState('pending');
  const [password,     setPassword]     = useState('');
  const [confirm,      setConfirm]      = useState('');
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');

  useEffect(() => {
    let cancelled = false;

    // RC 1.35.F2 Stage 2 P1 fix — strict gating: only flip to `ready`
    // when Supabase fires a PASSWORD_RECOVERY event OR the URL hash
    // explicitly carries `type=recovery`. A pre-existing logged-in
    // session is NOT a recovery context and must not unlock the
    // new-password form (security regression caught by iter_30).
    const hash = (window.location.hash || '').toLowerCase();
    const hasRecoveryToken =
      hash.includes('type=recovery') &&
      (hash.includes('access_token=') || hash.includes('code='));

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (cancelled) return;
        if (event === 'PASSWORD_RECOVERY' && session) {
          setPhase('ready');
        }
      },
    );

    if (hasRecoveryToken) {
      // Supabase JS may take ~50-200ms to emit PASSWORD_RECOVERY after
      // ingesting the fragment. Allow up to 4s before declaring invalid.
      const t = setTimeout(() => {
        if (cancelled) return;
        setPhase((p) => (p === 'pending' ? 'invalid' : p));
      }, 4000);
      return () => {
        cancelled = true;
        clearTimeout(t);
        subscription?.unsubscribe?.();
      };
    }

    // No recovery token in the URL → invalid immediately. We do NOT
    // call getSession(): a regular logged-in session must never gate
    // open the password-set form.
    setPhase('invalid');
    return () => {
      cancelled = true;
      subscription?.unsubscribe?.();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LEN) {
      setError(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) {
        setError(err.message || 'Could not update password. Please try again.');
        return;
      }
      // Clear the recovery session so the link can't be reused, then
      // route the user back to /login with a celebratory toast.
      await supabase.auth.signOut();
      toast.success('Password updated. Please sign in with your new password.');
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-ndor-canvas" data-testid="reset-password-page">
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <BrandLogo variant="symbol" size={64} />
            <p className="text-3xl font-serif-display font-semibold text-ndor-ink tracking-tight">
              NDOR<span className="text-gold-accent">Flow</span>
            </p>
          </div>

          {phase === 'pending' && (
            <div className="flex items-center justify-center gap-2 text-ndor-ink-muted text-sm" data-testid="reset-password-pending">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying reset link…
            </div>
          )}

          {phase === 'invalid' && (
            <div
              className="rounded-xl border border-ndor-error-border bg-ndor-error-light p-6 space-y-3 text-center"
              data-testid="reset-password-invalid"
            >
              <AlertCircle className="h-8 w-8 mx-auto text-ndor-error" />
              <h1 className="text-xl font-serif-display font-semibold text-ndor-ink">
                Reset link invalid or expired
              </h1>
              <p className="text-sm text-ndor-ink-secondary">
                Please request a new password reset link.
              </p>
              <Button asChild variant="outline" className="mt-2">
                <a href="/forgot-password" data-testid="reset-password-request-new">Request new link</a>
              </Button>
            </div>
          )}

          {phase === 'ready' && (
            <div className="rounded-xl border border-ndor-edge bg-ndor-surface p-6 space-y-5">
              <div className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-ndor-gold" />
                <h1 className="text-xl font-serif-display font-semibold text-ndor-ink">
                  Set a new password
                </h1>
              </div>
              <p className="text-sm text-ndor-ink-secondary">
                Choose a password of at least {MIN_PASSWORD_LEN} characters. After
                saving, you'll be signed out and asked to sign in again with your
                new password.
              </p>

              {error && (
                <div
                  className="rounded-lg bg-ndor-error-light border border-ndor-error-border p-3 text-sm text-ndor-error"
                  data-testid="reset-password-error"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-ndor-ink">New password</Label>
                  <PasswordInput
                    id="new-password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11"
                    data-testid="reset-password-new-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-ndor-ink">Confirm new password</Label>
                  <PasswordInput
                    id="confirm-password"
                    placeholder="Repeat your new password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="h-11"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 font-medium"
                  disabled={loading}
                  data-testid="reset-password-submit"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {loading ? 'Updating…' : 'Update password'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
      <LegalFooter variant="auth" />
    </div>
  );
}
